import { IconButton } from '@creditornot/cb-button';
import { CheckmarkCircleFilled, AlertCircleFilled, Cross, RotatingArrows } from '@creditornot/cb-icons';
import { colors, elevationCss, typographyCss } from '@creditornot/cb-ingredients';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import React, { ReactNode } from 'react';
import styled, { keyframes, css } from 'styled-components';

type Variant = 'success' | 'error' | 'loading';

type Props = {
  variant: Variant;
  children: React.ReactNode;
  onCloseButtonClick?: () => void;
};

const keyframesRotate = keyframes`
  100% {
    transform: rotate(360deg);
    transform-origin: center center;
  }
`;

const Root = styled.div`
  ${elevationCss(24)}
  min-width: 400px;
  display: flex;
  border-radius: 8px;
  background-color: ${cssThemedValue({ light: colors.salt100, dark: colors.pepper100 })};
  color: ${cssThemedValue({ light: colors.pepper100, dark: colors.salt100 })};
  padding: 16px;
`;

const IconContainer = styled.div<{ iconFill: { light: string; dark: string }; isLoading: boolean }>`
  flex-shrink: 0;
  padding-inline-end: 16px;
  display: flex;
  align-items: center;
  svg {
    fill: ${({ iconFill }) => cssThemedValue(iconFill)};
    width: 20px;
    ${({ isLoading }) =>
      isLoading &&
      css`
        animation: ${keyframesRotate} 1500ms infinite linear;
      `}
  }
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  ${typographyCss.Body3()};
  font-weight: 600;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const CloseButtonContainer = styled.div`
  display: flex;
  align-items: center;
  margin-inline-start: 16px;
`;

const iconPropsByVariant: Record<Variant, { icon: ReactNode; fill: { light: string; dark: string } }> = {
  success: {
    icon: <CheckmarkCircleFilled />,
    fill: { light: colors.lime100, dark: colors.lime100 },
  },
  error: {
    icon: <AlertCircleFilled />,
    fill: { light: colors.banana100, dark: colors.banana100 },
  },
  loading: {
    icon: <RotatingArrows />,
    fill: { light: colors.pepper100, dark: colors.salt100 },
  },
};

const CustomToast = ({ variant, children, onCloseButtonClick }: Props) => (
  <Root>
    <IconContainer iconFill={iconPropsByVariant[variant].fill} isLoading={variant === 'loading'}>
      {iconPropsByVariant[variant].icon}
    </IconContainer>
    <Content>{children}</Content>
    {onCloseButtonClick && (
      <CloseButtonContainer>
        <IconButton
          variant="neutral"
          size="tiny"
          alignment="iconEdges"
          background="transparent"
          onClick={onCloseButtonClick}
        >
          <Cross />
        </IconButton>
      </CloseButtonContainer>
    )}
  </Root>
);

export default CustomToast;
